/**
 * Projects API Client
 * 
 * Real API implementation for project management.
 * All endpoints connect to the backend API.
 */

import type {
  Project,
  ProjectType,
  ConstructionType,
  Client,
  Address,
  DefaultFinishes,
  ClientDesignation,
  WorkOrderDto,
  ProjectAdjustmentsResponse,
  ProjectInvoice,
  ProjectInvoicesResponse,
  ProjectExecutionDetailsDto,
  UpsertAdjustmentResponse,
  DeleteAdjustmentResponse
} from '@/types'
import { apiClient } from './client'
import { ApiError } from './client'
import { config } from '@/lib/config'

/**
 * Get work order for project
 * GET /api/projects/{projectId}/work-order
 */
export async function getProjectWorkOrder(projectId: string): Promise<WorkOrderDto> {
  return await apiClient.get<WorkOrderDto>(`/projects/${projectId}/work-order`)
}

export async function getProjectExecutionDetails(
  projectId: string
): Promise<ProjectExecutionDetailsDto> {
  return await apiClient.get<ProjectExecutionDetailsDto>(
    `/projects/${projectId}/execution-details`
  )
}

export async function upsertProjectExecutionDetails(
  projectId: string,
  data: {
    desiredInstallationDate: string | null
    floorKind: number | null
    floorNumber: number | null
    hasElevator: boolean | null
    needsLift: boolean | null
    needsMeasurements: boolean | null
    installersNote: string | null
  }
): Promise<ProjectExecutionDetailsDto> {
  return await apiClient.put<ProjectExecutionDetailsDto>(
    `/projects/${projectId}/execution-details`,
    data
  )
}

export async function previewProjectInvoicePdf(
  projectId: string,
  data: {
    invoiceType: 'deposit' | 'final' | 'standalone' | 'credit_note'
    amountWithTax?: number
    invoiceDate: string
    lines?: import('@/types').CreateInvoiceLineDto[]
    referencedInvoiceIds?: string[]
    // SOLY-CN-002 — Mode A/B preview support. BE already accepts these on
    // RenderInvoiceCreationPreviewPdfQuery; the FE just wasn't forwarding them.
    mode?: 'full_cancellation' | 'partial_proportional' | 'free_lines'
    partialAmountTtc?: number
    partialProportion?: number
    // SOLY-CN-003 — optional free-text credit note reason, shown on the PDF.
    reason?: string
  }
): Promise<Blob> {
  const url = `${config.apiBaseUrl}/projects/${projectId}/invoices/preview-pdf`

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/pdf',
    },
    body: JSON.stringify(data),
  })

  if (response.status === 401) {
    throw new ApiError(401, 'UNAUTHORIZED', 'Non authentifié')
  }

  if (!response.ok) {
    // Surface the BE error code (e.g. OVER_INVOICED) so pages can show an
    // actionable message instead of the generic preview failure.
    const errorData: { error?: { code?: string; message?: string }; code?: string; message?: string } =
      await response.json().catch(() => ({}))
    throw new ApiError(
      response.status,
      errorData.error?.code || errorData.code || 'INVOICE_PREVIEW_PDF_FAILED',
      errorData.error?.message || errorData.message || "Impossible de charger l'aperçu",
      errorData
    )
  }

  return await response.blob()
}

/**
 * Upsert project by type (Step 1)
 * Creates if doesn't exist, updates if exists
 * PUT /api/projects/{id}
 */
export async function upsertProjectType(
  projectId: string,
  type: ProjectType
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}`,
    { type }
  )
  return response
}

/**
 * Update client details (Step 2)
 * PUT /api/projects/{id}/billing/client
 */
export async function updateProjectClient(
  projectId: string,
  client: Omit<Client, 'id'>
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/billing/client`,
    client
  )
  return response
}

/**
 * Update site address (Step 3)
 * PUT /api/projects/{id}/site-address
 */
export async function updateProjectSiteAddress(
  projectId: string,
  address: Address
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/site-address`,
    address
  )
  return response
}

/**
 * Update billing address (Step 4)
 * PUT /api/projects/{id}/billing/address
 */
export async function updateProjectBillingAddress(
  projectId: string,
  data: { sameAsSite: boolean; address?: Address }
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/billing/address`,
    data
  )
  return response
}

/**
 * Update construction configuration (Step 6)
 * PUT /api/projects/{id}/billing/vat
 */
export async function updateProjectConstruction(
  projectId: string,
  data: { constructionType: ConstructionType }
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/billing/vat`,
    data
  )
  return response
}

/**
 * Update supplier configuration (Step 7)
 * PUT /api/projects/{id}/config/supplier
 */
export async function updateProjectSupplier(
  projectId: string,
  data: { supplier: string; modelReference?: string }
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/config/supplier`,
    data
  )
  return response
}

/**
 * Update finishes configuration (Step 8)
 * PUT /api/projects/{id}/config/finishes
 */
export async function updateProjectFinishes(
  projectId: string,
  finishes: DefaultFinishes
): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/config/finishes`,
    finishes
  )
  return response
}

 /**
  * Update project name
  * PUT /api/projects/{id}/name
  */
 export async function updateProjectName(
  projectId: string,
  name: string
 ): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/name`,
    { name }
  )
  return response
 }

 /**
  * Link project to an existing job site (true reuse)
  * PUT /api/projects/{id}/site
  */
 export async function updateProjectJobSite(
  projectId: string,
  data: { jobSiteId: string }
 ): Promise<Project> {
  const response = await apiClient.put<Project>(
    `/projects/${projectId}/site`,
    data
  )
  return response
 }

/**
 * @deprecated Use specific update functions instead (updateProjectClient, updateProjectSiteAddress, etc.)
 * Generic PATCH endpoint - kept for backward compatibility
 * PATCH /api/projects/{id}
 */
export async function patchProject(
  projectId: string,
  updates: Partial<Project>
): Promise<Project> {
  console.warn('patchProject is deprecated. Use specific update functions instead.')
  const response = await apiClient.patch<Project>(
    `/projects/${projectId}`,
    updates
  )
  return response
}

/**
 * Get project by ID
 * GET /api/projects/{id}
 */
export async function getProjectById(id: string): Promise<Project | null> {
  try {
    const response = await apiClient.get<Project>(`/projects/${id}`)
    return response
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Get all projects for current user
 * GET /api/projects
 */
export async function getProjects(): Promise<Project[]> {
  const response = await apiClient.get<{ projects: Project[] }>('/projects')
  return response.projects
}

/**
 * Delete a project
 * DELETE /api/projects/{id}
 */
export async function deleteProject(id: string): Promise<void> {
  await apiClient.delete(`/projects/${id}`)
}

/**
 * Get dynamic form options from backend
 * GET /api/config/options
 * 
 * Returns organization-specific options for dropdowns.
 */
export async function getProjectOptions(): Promise<{
  projectTypes: Array<{ value: ProjectType; label: string; icon: string }>
  designations: Array<{ value: ClientDesignation; label: string }>
  constructionTypes: Array<{ value: ConstructionType; label: string; description: string; icon: string }>
  suppliers: Array<{ id: string; name: string; isDefault: boolean }>
  materials: {
    facade: string[]
    carcass: string[]
    worktop: string[]
  }
  colors: string[]
  finishes: string[]
}> {
  const response = await apiClient.get<{
    projectTypes: Array<{ value: ProjectType; label: string; icon: string }>
    designations: Array<{ value: ClientDesignation; label: string }>
    constructionTypes: Array<{ value: ConstructionType; label: string; description: string; icon: string }>
    suppliers: Array<{ id: string; name: string; isDefault: boolean }>
    materials: {
      facade: string[]
      carcass: string[]
      worktop: string[]
    }
    colors: string[]
    finishes: string[]
  }>('/config/options')
  
  return response
}

/**
 * Calculate project totals
 * Note: This is calculated server-side. This client-side version is for display only.
 */
export function calculateProjectTotals(project: Project): {
  totalHT: number
  totalTTC: number
} {
  return {
    totalHT: project.totalBeforeTax,
    totalTTC: project.totalWithTax,
  }
}

// ============================================================================
// NEW ENDPOINTS - Flow 004 (Separated endpoints for performance)
// ============================================================================

/**
 * Get project adjustments (suppléments/réductions)
 * GET /api/projects/{id}/adjustments
 */
export async function getProjectAdjustments(projectId: string): Promise<ProjectAdjustmentsResponse> {
  try {
    const response = await apiClient.get<ProjectAdjustmentsResponse>(
      `/projects/${projectId}/adjustments`
    )
    return response
  } catch (error: unknown) {
    console.error('Error fetching project adjustments:', error)
    throw error
  }
}

/**
 * Get project invoices summary and history
 * GET /api/projects/{id}/invoices
 */
export async function getProjectInvoices(projectId: string): Promise<ProjectInvoicesResponse> {
  try {
    const response = await apiClient.get<ProjectInvoicesResponse>(
      `/projects/${projectId}/invoices`
    )
    return response
  } catch (error: unknown) {
    console.error('Error fetching project invoices:', error)
    throw error
  }
}

/**
 * SOLY-INV-001 — supprime définitivement la dernière facture de l'organisation
 * (réattribue son numéro). 204 si OK ; erreurs : 409 NOT_LAST_INVOICE /
 * CONCURRENCY_CONFLICT, 400 (CANNOT_DELETE_*, INVOICE_HAS_CREDIT_NOTE…).
 */
export async function deleteProjectInvoice(projectId: string, invoiceId: string): Promise<void> {
  try {
    await apiClient.delete<void>(`/projects/${projectId}/invoices/${invoiceId}`)
  } catch (error: unknown) {
    console.error('Error deleting project invoice:', error)
    throw error
  }
}

export interface InvoicePdfUrlDto {
  url: string
  expiresInSeconds: number
  filename: string
}

export interface QuotePdfUrlDto {
  url: string
  expiresInSeconds: number
  filename: string
}

export async function getProjectInvoicePdfUrl(
  projectId: string,
  invoiceId: string
): Promise<InvoicePdfUrlDto> {
  const response = await apiClient.get<InvoicePdfUrlDto>(
    `/projects/${projectId}/invoices/${invoiceId}/pdf-url`
  )
  return response
}

export async function getProjectQuotePdfUrl(
  projectId: string,
  quoteId: string
): Promise<QuotePdfUrlDto> {
  const response = await apiClient.get<QuotePdfUrlDto>(
    `/projects/${projectId}/quotes/${quoteId}/pdf-url`
  )
  return response
}

/**
 * Create a new invoice for a project
 * POST /api/projects/{id}/invoices
 */
// BE response shape (Soly.Invoicing.Application.Commands.InvoiceDto). Different
// from FE's ProjectInvoice listing shape — we map it below.
interface CreateInvoiceResponseDto {
  invoiceId: string
  invoiceNumber: string
  invoiceType: string
  totalWithTax?: number | null
  totalBeforeTax?: number | null
  totalVat?: number | null
  issuedDate: string
  // SOLY-CN-001 supersession surfaced on POST so listings can refresh badge
  // without an extra fetch.
  isSuperseded?: boolean
  supersededByInvoiceId?: string | null
  supersededAt?: string | null
}

export async function createProjectInvoice(
  projectId: string,
  data: {
    invoiceType: 'deposit' | 'final' | 'standalone' | 'credit_note'
    amountWithTax?: number
    invoiceDate: string
    lines?: import('@/types').CreateInvoiceLineDto[]
    referencedInvoiceIds?: string[]
    // SOLY-CN-001 credit note dispatch.
    mode?: import('@/types').CreditNoteMode
    partialAmountTtc?: number
    partialProportion?: number
    // SOLY-CN-003 — optional free-text credit note reason, shown on the PDF.
    reason?: string
  }
): Promise<ProjectInvoice> {
  // Backward compat — when caller emits a credit note via the legacy lines
  // path (Mode C) without setting mode, default explicitly so the BE handler
  // gets the FreeLines token instead of relying on its null fallback.
  // Matches SOLY-CN-001 mini-scope 04 AC #4.
  const payload =
    data.invoiceType === 'credit_note' && data.mode == null
      ? { ...data, mode: 'free_lines' as const }
      : data

  try {
    const r = await apiClient.post<CreateInvoiceResponseDto>(
      `/projects/${projectId}/invoices`,
      payload
    )
    return {
      id: r.invoiceId,
      number: r.invoiceNumber,
      type: r.invoiceType as ProjectInvoice['type'],
      amountWithTax: r.totalWithTax ?? 0,
      invoiceDate: r.issuedDate,
      status: 'generated',
      createdAt: new Date().toISOString(),
      isSuperseded: r.isSuperseded ?? false,
      supersededByInvoiceId: r.supersededByInvoiceId ?? null,
      supersededAt: r.supersededAt ?? null,
    }
  } catch (error: unknown) {
    console.error('Error creating project invoice:', error)
    throw error
  }
}

/**
 * Upsert adjustment (create if id is null, update if exists)
 * PUT /api/projects/{id}/adjustments/{adjustmentId}
 * 
 * Matches backend UpsertAdjustmentRequest
 * @param adjustmentId - UUID for update, 'new' or null for creation
 */
export async function upsertAdjustment(
  projectId: string,
  adjustmentId: string | null,
  adjustmentData: {
    name: string
    description?: string
    priceBeforeTax: number      // Always positive
    vatRate: number | null      // null = use project default
    isDiscount?: boolean        // Optional, derived from context if null
  }
): Promise<UpsertAdjustmentResponse> {
  try {
    const endpoint = adjustmentId
      ? `/projects/${projectId}/adjustments/${adjustmentId}`
      : `/projects/${projectId}/adjustments/new`
    
    const response = await apiClient.put<UpsertAdjustmentResponse>(
      endpoint,
      adjustmentData
    )
    return response
  } catch (error: unknown) {
    console.error('Error upserting adjustment:', error)
    throw error
  }
}

/**
 * Delete adjustment
 * DELETE /api/projects/{id}/adjustments/{adjustmentId}
 */
export async function deleteAdjustment(
  projectId: string,
  adjustmentId: string
): Promise<DeleteAdjustmentResponse> {
  try {
    const response = await apiClient.delete<DeleteAdjustmentResponse>(
      `/projects/${projectId}/adjustments/${adjustmentId}`
    )
    return response
  } catch (error: unknown) {
    console.error('Error deleting adjustment:', error)
    throw error
  }
}

/**
 * SOLY-REGIE-002 — « Retouche gratuite » : la sortie du dossier qu'on a décidé de ne pas facturer.
 *
 * Réversible par construction (patron Mark/Clear), gardée côté serveur par `invoice:write`.
 * 204 si OK ; 403 FORBIDDEN sans le droit ; 404 si le dossier n'appartient pas à l'organisation.
 */
export async function markProjectNotBilled(projectId: string): Promise<void> {
  await apiClient.post<void>(`/projects/${projectId}/invoices/not-billed`, {})
}

export async function clearProjectNotBilled(projectId: string): Promise<void> {
  await apiClient.delete<void>(`/projects/${projectId}/invoices/not-billed`)
}
